import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Download,
  Copy,
  CheckCircle,
  Loader2,
  FileText,
  Edit
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── CV Parser ────────────────────────────────────────────────────────────────
const parseCV = (text) => {
  if (!text) return { name: '', contact: [], sections: [] };

  const lines = text.split('\n').map(l => l.trim());

  const sectionRe = /^(objectif|profil professionnel|profil|expérience|expériences|expériences professionnelles?|formation|formations?|compétences?|compétences? clés?|compétences? techniques?|langues?|certifications?|références?|projets?|centres? d.intérêt|loisirs|informations? personnelles?|contact|résumé|éducation|réalisations?|activités?)/i;
  const allCapsRe = /^[A-ZÀÂÄÉÈÊËÎÏÔÙÛÜÇ\s]{4,}$/;

  const isSectionHeader = (line) => {
    if (!line || line.length > 60) return false;
    return sectionRe.test(line) || allCapsRe.test(line);
  };

  let name = '';
  let contact = [];
  let sections = [];
  let currentSection = null;
  let phase = 'header';

  for (const line of lines) {
    if (!line) continue;
    if (!name) { name = line; continue; }

    if (isSectionHeader(line)) {
      phase = 'sections';
      if (currentSection) sections.push(currentSection);
      currentSection = { title: line, items: [] };
      continue;
    }

    if (phase === 'header') {
      contact.push(line);
      if (contact.length >= 5) phase = 'sections'; // safety
    } else if (currentSection) {
      currentSection.items.push(line);
    }
  }

  if (currentSection) sections.push(currentSection);
  return { name, contact, sections };
};

// Sections that belong in the sidebar for the modern template
const SIDEBAR_SECTION_KEYS = /^(compétences?|langues?|certifications?|centres? d.intérêt|loisirs|informations? personnelles?|contact|activités?)/i;

// ─── Classic Template ─────────────────────────────────────────────────────────
const ClassicCV = ({ data }) => (
  <div style={{
    fontFamily: 'Georgia, "Times New Roman", serif',
    color: '#1a1a1a',
    padding: '28px 32px',
    background: '#fff',
    minHeight: '900px',
    lineHeight: 1.6
  }}>
    {/* Header */}
    <div style={{ borderBottom: '2px solid #1e3a5f', paddingBottom: '16px', marginBottom: '28px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0, color: '#1e3a5f' }}>{data.name}</h1>
      {data.contact.length > 0 && (
        <div style={{ marginTop: '6px', fontSize: '13px', color: '#555' }}>
          {data.contact.join('  ·  ')}
        </div>
      )}
    </div>

    {/* Sections */}
    {data.sections.map((sec, i) => (
      <div key={i} style={{ marginBottom: '22px' }}>
        <h2 style={{
          fontSize: '11px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: '#1e3a5f',
          borderBottom: '1px solid #c8d5e8',
          paddingBottom: '4px',
          marginBottom: '10px'
        }}>
          {sec.title}
        </h2>
        <div style={{ fontSize: '13px', color: '#333', whiteSpace: 'pre-wrap' }}>
          {sec.items.join('\n')}
        </div>
      </div>
    ))}
  </div>
);

// ─── Modern Template ──────────────────────────────────────────────────────────
const ModernCV = ({ data }) => {
  const sidebarSections = data.sections.filter(s => SIDEBAR_SECTION_KEYS.test(s.title));
  const mainSections = data.sections.filter(s => !SIDEBAR_SECTION_KEYS.test(s.title));

  return (
    <div style={{
      display: 'flex',
      fontFamily: '"Helvetica Neue", Arial, sans-serif',
      minHeight: '900px',
      background: '#fff'
    }}>
      {/* Sidebar */}
      <div style={{
        width: '220px',
        flexShrink: 0,
        background: '#1e3a5f',
        color: '#fff',
        padding: '36px 24px',
      }}>
        <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', margin: '0 0 6px 0', lineHeight: 1.3 }}>
          {data.name}
        </h1>
        {data.contact.map((c, i) => (
          <p key={i} style={{ fontSize: '11px', color: '#93b4d4', margin: '0 0 3px 0', lineHeight: 1.5 }}>{c}</p>
        ))}

        {sidebarSections.map((sec, i) => (
          <div key={i} style={{ marginTop: '24px' }}>
            <h2 style={{
              fontSize: '9px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              color: '#93b4d4',
              borderBottom: '1px solid #2d5a8e',
              paddingBottom: '4px',
              marginBottom: '8px'
            }}>
              {sec.title}
            </h2>
            <div style={{ fontSize: '12px', color: '#d1e3f5', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {sec.items.join('\n')}
            </div>
          </div>
        ))}
      </div>

      {/* Main column */}
      <div style={{ flex: 1, padding: '36px 36px 36px 32px' }}>
        {mainSections.map((sec, i) => (
          <div key={i} style={{ marginBottom: '22px' }}>
            <h2 style={{
              fontSize: '10px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: '#1e3a5f',
              borderBottom: '2px solid #1e3a5f',
              paddingBottom: '4px',
              marginBottom: '10px'
            }}>
              {sec.title}
            </h2>
            <div style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {sec.items.join('\n')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const OptimizedCVPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cvContent, setCvContent] = useState('');
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadAnalysis();
  }, [id]);

  const loadAnalysis = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analysis/${id}`, {
        withCredentials: true
      });
      setAnalysis(data);
      setCvContent(data.optimized_cv || '');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWord = () => {
    const data = parseCV(cvContent);
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Render each section exactly as ClassicCV does: items joined with \n, pre-wrap
    const sectionsHtml = data.sections.map(sec => {
      const itemsHtml = sec.items.map(line => {
        const l = esc(line);
        // Lines starting with "- " → bullet paragraph (matching the on-screen pre-wrap render)
        if (l.startsWith('- ')) {
          return `<p style="margin:3pt 0 3pt 12pt;font-size:10pt;color:#333;">• ${l.slice(2)}</p>`;
        }
        return `<p style="margin:2pt 0;font-size:10pt;color:#333;">${l}</p>`;
      }).join('');

      return `
        <div style="margin-bottom:16pt;">
          <h2 style="font-size:8pt;font-weight:bold;text-transform:uppercase;letter-spacing:0.12em;color:#1e3a5f;border-bottom:1pt solid #c8d5e8;padding-bottom:3pt;margin:0 0 8pt 0;">${esc(sec.title)}</h2>
          ${itemsHtml}
        </div>`;
    }).join('');

    const contactLine = data.contact.map(esc).join('&nbsp;&nbsp;·&nbsp;&nbsp;');

    const html = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>CV optimisé</title>
<style>
  body { font-family: Georgia, "Times New Roman", serif; color: #1a1a1a; line-height: 1.6; margin: 2.2cm 2.5cm; }
  @page { margin: 2.2cm 2.5cm; }
</style>
</head>
<body>
  <div style="border-bottom:2pt solid #1e3a5f;padding-bottom:12pt;margin-bottom:20pt;">
    <h1 style="font-size:22pt;font-weight:bold;color:#1e3a5f;margin:0 0 5pt 0;font-family:Georgia,serif;">${esc(data.name)}</h1>
    <p style="margin:0;font-size:10pt;color:#555;">${contactLine}</p>
  </div>
  ${sectionsHtml}
</body>
</html>`;

    const blob = new Blob([html], { type: 'application/msword' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cv_optimise_jobmatch.doc';
    a.click();
    window.URL.revokeObjectURL(url);
  };


  const handleCopy = () => {
    navigator.clipboard.writeText(cvContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!analysis?.optimized_cv) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Header />
        <div className="pt-32 pb-20 text-center">
          <p className="text-zinc-600">CV optimisé non disponible</p>
          <Button onClick={() => navigate(`/resultats/${id}`)} className="mt-4">
            Retour aux résultats
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const parsedCV = parseCV(cvContent);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back */}
          <button
            onClick={() => navigate(`/resultats/${id}`)}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux résultats
          </button>

          {/* Header bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1
                  className="text-2xl font-semibold text-zinc-900"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                  data-testid="cv-title"
                >
                  CV optimisé
                </h1>
                <p className="text-zinc-500 text-sm">Adapté à l'offre d'emploi</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => setEditing(!editing)}
                className="border-zinc-200"
                data-testid="edit-button"
              >
                <Edit className="h-4 w-4 mr-2" />
                {editing ? 'Aperçu' : 'Modifier'}
              </Button>
              <Button
                variant="outline"
                onClick={handleCopy}
                className="border-zinc-200"
                data-testid="copy-button"
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                    Copié !
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copier
                  </>
                )}
              </Button>
              <Button
                onClick={handleDownloadWord}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="download-word-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Word (.doc)
              </Button>
            </div>
          </div>

          {/* CV display */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="cv-content">
            {editing ? (
              <div className="p-6">
                <Textarea
                  value={cvContent}
                  onChange={(e) => setCvContent(e.target.value)}
                  className="min-h-[600px] font-mono text-sm border-zinc-200"
                  data-testid="cv-editor"
                />
              </div>
            ) : (
              <ClassicCV data={parsedCV} />
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OptimizedCVPage;
